/**
 * UtilityProviderFormDrawer - Slide-out drawer for creating/editing utility providers
 */

import { useState, useEffect, useMemo } from 'react';
import { AdminFormDrawer } from './AdminFormDrawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { UtilityProvider, UtilityProviderFormData, CountryCode } from '@/types/utilityProviders';
import {
  COUNTRIES,
  getRegionsForCountry,
  getRegionName,
} from '@/types/utilityProviders';

interface UtilityProviderFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UtilityProviderFormData) => Promise<void>;
  provider?: UtilityProvider | null;
  isSubmitting?: boolean;
}

export function UtilityProviderFormDrawer({
  open,
  onClose,
  onSubmit,
  provider,
  isSubmitting = false,
}: UtilityProviderFormDrawerProps) {
  const isEditing = !!provider;

  // Form state
  const [formData, setFormData] = useState<UtilityProviderFormData>({
    name: '',
    countryCode: 'US',
    regionCode: '',
    website: '',
    description: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get available regions based on selected country
  const availableRegions = useMemo(() => {
    return getRegionsForCountry(formData.countryCode);
  }, [formData.countryCode]);

  // Initialize form when provider changes
  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name,
        countryCode: provider.countryCode,
        regionCode: provider.regionCode || '',
        website: provider.website || '',
        description: provider.description || '',
      });
    } else {
      // Reset to defaults for new provider
      setFormData({
        name: '',
        countryCode: 'US',
        regionCode: '',
        website: '',
        description: '',
      });
    }
    setErrors({});
  }, [provider, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      // Add protocol if missing
      const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
      new URL(urlToTest);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Normalize website URL
    const normalizedData = { ...formData };
    if (normalizedData.website && !normalizedData.website.match(/^https?:\/\//)) {
      normalizedData.website = `https://${normalizedData.website}`;
    }

    await onSubmit(normalizedData);
  };

  const updateField = <K extends keyof UtilityProviderFormData>(
    field: K,
    value: UtilityProviderFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <AdminFormDrawer
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Utility Provider' : 'Add Utility Provider'}
      description={
        isEditing
          ? 'Update utility provider information'
          : 'Add a new utility provider to the system'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Provider Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g., Pacific Gas & Electric"
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
        </div>

        {/* Location Section */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-sm text-foreground">Location</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="countryCode">Country *</Label>
              <Select
                value={formData.countryCode}
                onValueChange={(v) => {
                  updateField('countryCode', v as CountryCode);
                  // Clear region when country changes
                  updateField('regionCode', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COUNTRIES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* State/Province/Region */}
            <div className="space-y-2">
              <Label htmlFor="regionCode">
                {formData.countryCode === 'UK'
                  ? 'Region'
                  : formData.countryCode === 'CA'
                    ? 'Province'
                    : 'State'}
              </Label>
              <Select
                value={formData.regionCode || 'none'}
                onValueChange={(v) => updateField('regionCode', v === 'none' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={`Select ${
                      formData.countryCode === 'UK'
                        ? 'region'
                        : formData.countryCode === 'CA'
                          ? 'province'
                          : 'state'
                    }`}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {availableRegions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {getRegionName(formData.countryCode, region)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-sm text-foreground">
            Additional Information (Optional)
          </h3>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="text"
              value={formData.website}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder="e.g., pge.com or https://pge.com"
              className={errors.website ? 'border-destructive' : ''}
            />
            {errors.website && (
              <p className="text-sm text-destructive">{errors.website}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Protocol (https://) will be added automatically if missing
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Brief description or notes about this provider"
              rows={3}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-[#8FB560] hover:bg-[#7a9d4e]"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditing ? (
              'Update Provider'
            ) : (
              'Add Provider'
            )}
          </Button>
        </div>
      </form>
    </AdminFormDrawer>
  );
}
